import { ratioToPercent } from '@klareospace/pharos-lib-utils';
import type { Import, ImportDetail, ImportLineErrorCode } from '@klareospace/pharos-lib-types';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Narrowest shape read by `countRejected` / `computeSuccessRate` — both `ImportHistoryItem` and `ImportDetail` satisfy it. */
export type ImportMetricsSource = Pick<Import, 'linesCount'>;

/** One rejected-row entry, as rendered by the report's rejected-rows table — one row per `ImportLineError`. */
export interface RejectedRow {
  /** class-validator constraint keys — set only for `validation_error` entries; absent on older imports and other error codes. */
  constraints?: string[];
  errorCode: ImportLineErrorCode;
  field?: string;
  lineIndex: number;
  message: string;
  /** i18n interpolation arguments for `messageKey`, pre-stringified. Set alongside `messageKey`; absent when `messageKey` is absent. */
  messageArgs?: Record<string, string>;
  /** Stable machine key identifying `message` — set on `parse_error` and `scope_error` entries; absent on older imports and on other error codes. */
  messageKey?: string;
  reference?: string;
  value?: string;
}

// ─── Utils ────────────────────────────────────────────────────────────────────

/** Total rejected lines across every rejection reason. */
export function countRejected(importDoc: ImportMetricsSource): number {
  const { createError, parseError, updateError } = importDoc.linesCount;
  return parseError + createError + updateError;
}

/**
 * Success rate as (created + updated) / total — an upsert (PHA-588) counts as a success, not a
 * failure. Returns 0 when `total` is zero rather than dividing by zero.
 */
export function computeSuccessRate(importDoc: ImportMetricsSource): number {
  const { created, total, updated } = importDoc.linesCount;
  return total > 0 ? ratioToPercent((created + updated) / total, 0) : 0;
}

/** Maps an `ImportDetail`'s `errors.lines` to the rejected-rows table's row shape. */
export function flattenImportLineErrors(importDoc: ImportDetail): RejectedRow[] {
  return importDoc.errors.lines.map((line) => ({
    constraints: line.constraints,
    errorCode: line.code,
    field: line.field,
    lineIndex: line.lineIndex,
    message: line.message,
    messageArgs: line.messageArgs,
    messageKey: line.messageKey,
    reference: line.reference,
    value: line.value,
  }));
}

/**
 * True when the import hit the stored line-error cap (`MAX_STORED_LINE_ERRORS`) — distinct from the
 * smaller display cap. Derived by the backend.
 */
export function hasReachedLineErrorStorageLimit(importDoc: ImportDetail): boolean {
  return importDoc.lineErrorsTruncated;
}
